import { MediaType } from "../types";
import { MediaItemCard } from "./media-item-card/MediaItemCard";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import styled from "@emotion/styled";
import { SortableList } from "@kaltura/ds-react-drag-and-drop";
import { SortableItemType } from "@kaltura/ds-react-drag-and-drop/src/lib/sortable-list/SortableItem";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

interface ContentListProps {
    media: MediaType[];
    onMediaUpdate?: (updatedMedia: MediaType[]) => void;
}

const ContentListContainer = styled("div")(({ theme }) => ({
    border: `1px solid ${theme.kaltura.palette.tone4}`,
    borderBottom: "none",
    borderRadius: theme.kaltura.shape.roundness1,
    overflow: "hidden",
}));

/**
 * ContentList Component
 *
 * This component renders a sortable list of playlist's media items that can be reordered via drag and drop
 * and removed individually. It's used in the EditPlaylist component to manage playlist content.
 * it maintains state through parent component via callbacks.
 */
export const ContentList = (props: ContentListProps) => {
    const { media, onMediaUpdate } = props;
    const theme = useTheme();

    function handleRemove(itemToRemoveId: string) {
        // Create updated media list by filtering out the removed item
        // and notify parent component of the updated media list
        const updatedMedia = media.filter((item) => item.uniqueId !== itemToRemoveId);
        onMediaUpdate?.(updatedMedia);
    }

    const sendButtonAnalytics = useButtonAnalytics();

    const handleOrderChange = (newItems: SortableItemType[]) => {
        // Create a new array of MediaType items in the new order
        const orderedIds = newItems.map((item) => item.id);
        const updatedMedia = orderedIds
            .map((id) => media.find((item) => item.uniqueId === id))
            .filter((item): item is MediaType => item !== undefined);
        // send analytics for reorder action
        sendButtonAnalytics("Reorder Media Items", ButtonClickAnalyticsType.EDIT);
        // Notify parent component of the reordered media list
        onMediaUpdate?.(updatedMedia);
    };

    // Generate sortable items from the media prop
    const items = media
        .filter((item): item is MediaType & { uniqueId: string } => !!item.uniqueId)
        .map((item) => ({
            component: <MediaItemCard key={item.uniqueId} item={item} onRemove={() => handleRemove(item.uniqueId)} />,
            id: item.uniqueId,
        }));

    return (
        <ContentListContainer className={"kms-ds-edit-playlist-content-sortable-list"}>
            <SortableList items={items} onOrderChange={handleOrderChange} borderColor={theme.kaltura.palette.tone4} />
        </ContentListContainer>
    );
};
